import { Caption } from "@bcc-ui/ui-components";
import classNames from "classnames";
import { ErrorMessage, Form, Formik, FormikConfig, FormikProps, useFormikContext } from "formik";
import React from "react";
import { useTranslation } from "react-i18next";
import { array, object, string } from "yup";

import AuthorityView from "./AuthorityView";
import style from "./manage-role.scss";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import { NOTES_MAX_LENGTH } from "domain/globalConstants";
import { Authority, Targets } from "domain/roles";
import { rolesService } from "services/roles/RolesService";
import form from "styles/form.scss";

import testIds from "testIds.json";

interface Props {
    name: string;
    description: string;
    authorities: Authority[];
    targets?: Targets;
    isManager: boolean;
    isEditing?: boolean;
    submitEventHandler?: (values: FormValues) => Promise<void>;
    isViewRole: boolean;
    resetCounter: number;
    formRef: React.Ref<FormikProps<FormValues>>;
    onErrorChange: (hasError: boolean) => void;
}

export interface FormValues {
    name: string;
    description?: string;
    authorities: Authority[];
    targets?: Targets;
}

const NAME_MIN_LENGTH = 2;
const NAME_MAX_LENGTH = 32;
export const MANAGE_ROLE_FORM_ID = "manageRoleSubpageForm";

// Formik skips the submit handler when validation fails, so watching its errors is the only way to cover both
// validation errors and the ones set by the submit handler.
function FormErrorReporter(props: { onErrorChange: (hasError: boolean) => void }) {
    const { errors } = useFormikContext<FormValues>();
    const hasError = Object.keys(errors).length > 0;

    React.useEffect(() => {
        props.onErrorChange(hasError);
    }, [hasError, props.onErrorChange]);

    return null;
}

export default function ManageRoleSubPageForm(props: Props): JSX.Element {
    const { t } = useTranslation();
    const [loading, setLoading] = React.useState<boolean>(false);
    const abortController = new AbortController();
    const submitHandler: FormikConfig<FormValues>["onSubmit"] = async (values, { setErrors, setSubmitting }) => {
        setSubmitting(false);
        if (values.name !== null) {
            setLoading(true);
            if (values.name !== props.name || !props.isEditing) {
                const matchingRoles = await rolesService.fetchRoles(abortController, values.name);
                if (matchingRoles.roles.length === 0) {
                    await props.submitEventHandler?.(values);
                    setSubmitting(true);
                } else {
                    setLoading(false);
                    setErrors({ name: t("ManageRoleForm.roleNameAlreadyExists") });
                }
            } else {
                await props.submitEventHandler?.(values);
                setSubmitting(true);
            }
        } else {
            setLoading(false);
            setErrors({ name: t("ManageRoleForm.roleNameNotAvailable") });
        }
    };
    const [descriptionCharactersLeft, setDescriptionCharactersLeft] = React.useState(NOTES_MAX_LENGTH);
    const descriptionChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
        setDescriptionCharactersLeft(event.target.maxLength - event.target.value.length);
    };
    const nameValidationMessage = t("ManageRoleForm.roleNameLength", {
        minLength: NAME_MIN_LENGTH,
        maxLength: NAME_MAX_LENGTH,
    });

    return (
        <Formik<FormValues>
            innerRef={props.formRef}
            initialValues={{
                name: props.name,
                description: props.description,
                authorities: [...props.authorities],
                targets: props.targets,
            }}
            onSubmit={submitHandler}
            validationSchema={object().shape({
                name: string()
                    .min(NAME_MIN_LENGTH, nameValidationMessage)
                    .max(NAME_MAX_LENGTH, nameValidationMessage)
                    .required(t("ManageRoleForm.roleNameRequired")),
                authorities: array().of(string()).min(1, t("ManageRoleForm.authoritiesRequired")),
            })}
            validateOnChange={false}
            validateOnBlur={false}
        >
            {({ values, errors, handleChange, handleBlur, setFieldValue }: FormikProps<FormValues>) => {
                const loader = loading ? (
                    <div className={style.loaderContainer}>
                        <LoadingIndicator small={true} />
                    </div>
                ) : null;

                return (
                    <Form id={MANAGE_ROLE_FORM_ID}>
                        <FormErrorReporter onErrorChange={props.onErrorChange} />
                        <div className={form.formFields}>
                            {!props.isViewRole && (
                                <>
                                    <label htmlFor="name" className={form.label}>
                                        {t("ManageRoleForm.roleName")}
                                    </label>

                                    <div className={form.formFieldsColumnDirection}>
                                        <input
                                            id="name"
                                            className={classNames(form.input, form.fixedWidthInput, {
                                                [form.inputError]: errors.name,
                                            })}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.name}
                                            data-testid={testIds.workArea.roles.manageRoleDialog.roleNameInput.itself}
                                        />
                                        {loader}
                                        <div
                                            className={form.error}
                                            data-testid={
                                                testIds.workArea.roles.manageRoleDialog.roleNameInput.errorLabel
                                            }
                                        >
                                            <ErrorMessage name="name" />
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {!props.isViewRole && (
                            <div className={form.formFields}>
                                <div className={form.formFieldsColumnDirection}>
                                    <span className={form.optional}>{t("Common.optional")}</span>
                                    <label htmlFor="description" className={classNames(form.label)}>
                                        {t("ManageRoleForm.description")}
                                    </label>
                                </div>
                                <div className={style.descriptionContainer}>
                                    <textarea
                                        id="description"
                                        className={classNames(
                                            form.input,
                                            form.fixedWidthInput,
                                            style.descriptionHeight
                                        )}
                                        maxLength={NOTES_MAX_LENGTH}
                                        onChange={(e) => {
                                            descriptionChangeHandler(e);
                                            handleChange(e);
                                        }}
                                        value={values.description}
                                        data-testid={testIds.workArea.roles.manageRoleDialog.descriptionInput.itself}
                                    />
                                    <span className={form.optional}>
                                        {t("Common.charactersLeft", {
                                            remainingCharacters: descriptionCharactersLeft.toString(),
                                            descriptionMaxLength: NOTES_MAX_LENGTH.toString(),
                                        })}
                                    </span>
                                </div>
                            </div>
                        )}

                        <AuthorityView
                            authorities={values.authorities}
                            targets={values.targets}
                            onView={props.isViewRole}
                            isManager={props.isManager}
                            setFieldValue={setFieldValue}
                            resetCounter={props.resetCounter}
                        />
                        <Caption errorStyle testId={testIds.workArea.roles.manageRoleDialog.authorities.errorLabel}>
                            <ErrorMessage name="authorities" />
                        </Caption>
                    </Form>
                );
            }}
        </Formik>
    );
}
